import { useSelector } from "react-redux";
import { ShopFilter } from "@b2bportal/air-shopping-api";
import {
  FlightSearchQueryParams,
  type FlightSearchQueryParamValues,
  type IPassengerCounts,
  type ITripTerminus,
  SliceStopCountFilter,
} from "@b2bportal/core-types";
import {
  createQueryParams,
  QueryParamType,
  useParseQueryParams,
} from "@b2bportal/core-utilities";
import { toJsDate } from "@hopper-b2b/common-utils";

import { TripCategory } from "@overrides/types";
import type { FlightSearchValues } from "../../search/store";
import { getSearchState } from "../../search/store/selectors";

export const useGetShopQueryParams = () => {
  const params = {
    [FlightSearchQueryParams.ORIGIN]: {
      type: QueryParamType.requiredString,
    },
    [FlightSearchQueryParams.DESTINATION]: {
      type: QueryParamType.requiredString,
    },
    [FlightSearchQueryParams.DEPARTURE_DATE]: {
      type: QueryParamType.requiredDate,
    },
    [FlightSearchQueryParams.RETURN_DATE]: {
      type: QueryParamType.optionalDate,
    },
    [FlightSearchQueryParams.TRIP_CATEGORY]: {
      type: QueryParamType.optionalString,
    },
    [FlightSearchQueryParams.ADULT_COUNT]: {
      type: QueryParamType.optionalNumber,
    },
    [FlightSearchQueryParams.CHILD_COUNT]: {
      type: QueryParamType.optionalNumber,
    },
    [FlightSearchQueryParams.INFANT_IN_SEAT_COUNT]: {
      type: QueryParamType.optionalNumber,
    },
    [FlightSearchQueryParams.INFANT_IN_LAP_COUNT]: {
      type: QueryParamType.optionalNumber,
    },
    [FlightSearchQueryParams.NO_LCC]: {
      type: QueryParamType.optionalBoolean,
    },
    [FlightSearchQueryParams.STOPS_OPTION]: {
      type: QueryParamType.optionalString,
    },
  };

  return useParseQueryParams(params);
};

/**
 * Hook to check if the current search is the same as the one in the URL
 * by comparing the search values in the redux store with the URL search params
 */

export const useIsSameSearch = () => {
  const {
    origin,
    destination,
    departureDate,
    returnDate,
    tripCategory,
    adultCount,
    childCount,
    infantInSeatCount,
    infantOnLapCount,
    stopsOption,
    noLCC,
  } = useGetShopQueryParams();

  const searchValues = useSelector(getSearchState);
  const activeTripFilter = getTripFilter(stopsOption, noLCC);
  /** TODO: compare the rest of the search values related with passenger selection */
  const isSameSearch =
    isSameLocation(origin, searchValues.origin) &&
    isSameLocation(destination, searchValues.destination) &&
    isSameDate(departureDate, searchValues.departureDate) &&
    isSameDate(returnDate, searchValues.returnDate) &&
    searchValues.tripCategory === tripCategory &&
    isSameNumber(adultCount, searchValues.passengerCounts.adultsCount) &&
    isSameNumber(childCount, searchValues.passengerCounts.childrenCount) &&
    isSameNumber(
      infantInSeatCount,
      searchValues.passengerCounts.infantsInSeatCount
    ) &&
    isSameNumber(
      infantOnLapCount,
      searchValues.passengerCounts.infantsOnLapCount
    ) &&
    activeTripFilter === searchValues.tripFilter;

  return isSameSearch;
};
const isSameNumber = (param: number | undefined | null, value: number) => {
  const parsedParam = param == null ? 0 : param;
  const valueAsNumber = isNaN(value) ? 0 : value;
  return valueAsNumber === parsedParam;
};

const isSameLocation = (code?: string, location?: ITripTerminus) => {
  return (
    (code == null && location == null) ||
    (code != null && location != null && location.id.code.code === code)
  );
};

const isSameDate = (queryParamDate?: string | null, storeDate?: string) => {
  return (
    (queryParamDate == null && storeDate == null) ||
    (queryParamDate != null &&
      storeDate != null &&
      storeDate === queryParamDate)
  );
};

export const getTripFilter = (
  stopOptions: string | null | undefined,
  noLCC: boolean | null | undefined
) => {
  if (stopOptions === SliceStopCountFilter.NONE && noLCC === true)
    return ShopFilter.NonStopNoLCC;
  else if (stopOptions === SliceStopCountFilter.NONE) return ShopFilter.NonStop;
  else if (noLCC === true) return ShopFilter.NoLCC;
  else return ShopFilter.NoFilter;
};

export type BuildFlightShopQueryArgs =
  | {
      tripCategory: TripCategory.ROUND_TRIP;
      origin: ITripTerminus;
      destination: ITripTerminus;
      passengerCounts?: IPassengerCounts;
      departureDate: Date | string;
      returnDate: Date | string;
      stopsOption?: SliceStopCountFilter;
      noLCC?: boolean;
    }
  | {
      tripCategory: TripCategory.ONE_WAY;
      origin: ITripTerminus;
      destination: ITripTerminus;
      passengerCounts?: IPassengerCounts;
      departureDate: Date | string;
      stopsOption?: SliceStopCountFilter;
      noLCC?: boolean;
    };

export const buildFlightShopQuery = (params: BuildFlightShopQueryArgs) => {
  const {
    passengerCounts,
    origin,
    destination,
    departureDate,
    stopsOption,
    noLCC,
  } = params;

  try {
    const flightShopQueryObject = {
      [FlightSearchQueryParams.ORIGIN]: origin.id.code.code,
      [FlightSearchQueryParams.DESTINATION]: destination.id.code.code,
      [FlightSearchQueryParams.DEPARTURE_DATE]: toJsDate(departureDate),
      ...(params.tripCategory === TripCategory.ROUND_TRIP
        ? {
            [FlightSearchQueryParams.RETURN_DATE]: toJsDate(params.returnDate),
          }
        : {}),
      [FlightSearchQueryParams.TRIP_CATEGORY]: params.tripCategory,
      [FlightSearchQueryParams.STOPS_OPTION]: stopsOption,
      [FlightSearchQueryParams.ADULT_COUNT]: passengerCounts?.adultsCount,
      [FlightSearchQueryParams.CHILD_COUNT]: passengerCounts?.childrenCount,
      [FlightSearchQueryParams.INFANT_IN_LAP_COUNT]:
        passengerCounts?.infantsOnLapCount,
      [FlightSearchQueryParams.INFANT_IN_SEAT_COUNT]:
        passengerCounts?.infantsInSeatCount,
      [FlightSearchQueryParams.NO_LCC]: noLCC,
    };
    return createQueryParams(flightShopQueryObject).toString();
  } catch (e) {
    console.error(e);
  }
};

export const buildFlightShopValuesFromQueryParams = (
  queryParams: Partial<FlightSearchQueryParamValues>
): Omit<FlightSearchValues, "origin" | "destination"> => {
  const {
    departureDate,
    returnDate,
    tripCategory,
    adultCount,
    childCount,
    infantInSeatCount,
    infantOnLapCount,
    stopsOption,
    noLCC,
  } = queryParams;

  // Origin and destination are not returned given you can not build a location from a code
  return {
    departureDate,
    returnDate: returnDate ?? undefined,
    tripCategory:
      tripCategory === TripCategory.ONE_WAY
        ? TripCategory.ONE_WAY
        : TripCategory.ROUND_TRIP,
    passengerCounts: {
      adultsCount: adultCount != null ? adultCount : 0,
      childrenCount: childCount != null ? childCount : 0,
      infantsInSeatCount: infantInSeatCount != null ? infantInSeatCount : 0,
      infantsOnLapCount: infantOnLapCount != null ? infantOnLapCount : 0,
    },
    tripFilter: getTripFilter(stopsOption, noLCC),
    multicityRoutes: [],
  };
};
